import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { usePartyWithDetails } from '@/hooks/usePartyWithDetails'
import { usePartyDistrictStatesWithDistricts } from '@/hooks/usePartyDistrictStatesWithDistricts'
import { PartyStrengthTable } from '@/components/party/PartyStrengthTable'
import { Button } from '@/components/ui/button'

export default function PartyDistrictsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { party, isLoading: partyLoading, error: partyError } = usePartyWithDetails(id)
  const { districtStates, isLoading: districtStatesLoading } =
    usePartyDistrictStatesWithDistricts(id)

  if (partyLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-5xl py-8 px-4">
          <div className="space-y-6 animate-pulse">
            <div className="h-8 bg-muted rounded w-48" />
            <div className="h-96 bg-muted rounded" />
          </div>
        </div>
      </div>
    )
  }

  if (partyError) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-5xl py-8 px-4">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-destructive mb-2">Error Loading Party</h1>
            <p className="text-muted-foreground">{partyError.message}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!party) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-5xl py-8 px-4">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold mb-2">Party Not Found</h1>
            <p className="text-muted-foreground">
              The party you're looking for doesn't exist.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const isOwner = user?.id === party.user_id

  // Only party owners can see the full district breakdown
  if (!isOwner) {
    navigate(`/parties/${id}`)
    return null
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-5xl py-8 px-4">
        <div className="space-y-6">
          {/* Header with back link */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/parties/${id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
            </Button>
          </div>

          {/* Party name header */}
          <div className="flex items-center gap-3">
            <div
              className="h-6 w-6 rounded-full"
              style={{ backgroundColor: party.color }}
            />
            <h1 className="text-2xl font-bold">{party.name} - District Strength</h1>
          </div>

          {/* Full table */}
          <PartyStrengthTable
            districtStates={districtStates || []}
            isLoading={districtStatesLoading}
          />
        </div>
      </div>
    </div>
  )
}
